import dayjs from '@/helpers/dayjs';
import { yupAddress, yupBoolean, yupMixed, yupNumber, yupObject, yupPeriodicValue, yupString } from '../../yup';
import { DepositType, ErhlPropertyType, PropertyPurpose, StampDutyStatus } from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';

import type { DepositTypeEnum, PropertyDetailEntry, StampDutyStatusEnum } from '@/types';

type ErhlPropertyTypeEnum = (typeof ErhlPropertyType)[keyof typeof ErhlPropertyType];

const { isNegativeGearingFy27Enabled } = useFeatureFlags();

/**
 * Validation schema for ERHL supporting property details.
 * Supporting properties are the equity security — always collected.
 */
export const erhlSupportingPropertySchema = yupObject<PropertyDetailEntry>({
  propertyType: yupMixed<ErhlPropertyTypeEnum>().required().label('Property type'),

  address: yupAddress().required().label('Property address'),

  propertyPurpose: yupString().required().label('Property purpose'),

  receiveRentalIncome: yupBoolean()
    .when('propertyPurpose', {
      is: PropertyPurpose.INVESTMENT_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Rental income response'),

  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Rental income'),

  // Negative Gearing (FY27) — only enforced when the feature flag is enabled
  isNewBuild: yupBoolean()
    .when('propertyPurpose', ([propertyPurpose], schema) =>
      isNegativeGearingFy27Enabled() && propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY
        ? schema.required()
        : schema,
    )
    .label('New build response'),

  isContractExchanged: yupBoolean()
    .when(['propertyPurpose', 'isNewBuild'], ([propertyPurpose, isNewBuild], schema) =>
      isNegativeGearingFy27Enabled() && propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY && isNewBuild === false
        ? schema.required()
        : schema,
    )
    .label('Contract exchange date response'),

  supportingPropertyValue: yupNumber().required().label('Estimated property value'),

  outstandingLoanAmount: yupNumber().required().label('Outstanding mortgage'),
});

/**
 * Validation schema for ERHL purchase property details.
 * Only shown when loan purpose includes "Purchase new property".
 */
export const erhlPurchasePropertySchema = yupObject<PropertyDetailEntry>({
  propertyType: yupMixed<ErhlPropertyTypeEnum>().required().label('Property type'),

  address: yupAddress().required().label('Property address'),

  propertyPurpose: yupString()
    .when('propertyType', ([propertyType], schema) =>
      propertyType === ErhlPropertyType.RETIREMENT_LAND_LEASE_COMMUNITY_HOME ? schema : schema.required(),
    )
    .label('Property purpose'),

  receiveRentalIncome: yupBoolean()
    .when('propertyPurpose', {
      is: PropertyPurpose.INVESTMENT_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Rental income response'),

  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected rental income'),

  contractForSale: yupBoolean().required().label('Contract for sale response'),

  // Negative Gearing (FY27) — only enforced when the feature flag is enabled
  isNewBuild: yupBoolean()
    .when('propertyPurpose', ([propertyPurpose], schema) =>
      isNegativeGearingFy27Enabled() && propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY
        ? schema.required()
        : schema,
    )
    .label('New build response'),

  isContractExchanged: yupBoolean()
    .when(
      ['propertyPurpose', 'isNewBuild', 'contractForSale'],
      ([propertyPurpose, isNewBuild, contractForSale], schema) =>
        isNegativeGearingFy27Enabled() &&
        propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY &&
        isNewBuild === false &&
        contractForSale === true
          ? schema.required()
          : schema,
    )
    .label('Contract exchange date response'),

  settlementDate: yupString()
    .when('contractForSale', ([contractForSale], schema) => {
      if (contractForSale === undefined) return schema;
      return schema
        .required()
        .isoDate()
        .test('validDate', '${path} must be within 12 months', (value: string) => {
          const date = dayjs(value);
          return date.isAfter(dayjs(), 'day') && date.isBefore(dayjs().add(1, 'year'), 'day');
        });
    })
    .label('Settlement date'),

  purchaseAmount: yupNumber()
    .when('contractForSale', {
      is: (val: boolean) => val !== undefined,
      then: (schema) => schema.required(),
    })
    .label('Purchase price'),

  depositAmount: yupNumber()
    .when(['contractForSale', 'purchaseAmount'], ([contractForSale, purchaseAmount], schema) => {
      if (contractForSale === undefined) return schema;
      return schema.max(purchaseAmount, 'The deposit cannot be more than the purchase price').required();
    })
    .label('Deposit amount'),

  depositType: yupMixed<DepositTypeEnum>()
    .oneOf(Object.values(DepositType))
    .when('contractForSale', {
      is: (val: boolean) => val !== undefined,
      then: (schema) => schema.required(),
    })
    .label('Deposit payment method'),

  stampDutyStatus: yupMixed<StampDutyStatusEnum>()
    .when(['contractForSale', 'propertyType'], ([contractForSale, propertyType], schema) => {
      if (contractForSale === undefined || propertyType === ErhlPropertyType.RETIREMENT_LAND_LEASE_COMMUNITY_HOME) {
        return schema;
      }
      const baseSchema = schema.required().oneOf(Object.values(StampDutyStatus));
      if (contractForSale) return baseSchema;
      return baseSchema.oneOf(Object.values(StampDutyStatus).filter((item) => item !== StampDutyStatus.ALREADY_PAID));
    })
    .label('Stamp duty response'),

  transferFeeCovered: yupBoolean()
    .when('contractForSale', {
      is: (val: boolean) => val !== undefined,
      then: (schema) => schema.required(),
    })
    .label('Transfer fee'),

  erhlSavingsAmount: yupNumber().min(0).optional().label('Additional contributions amount'),

  erhlGiftedAmount: yupNumber()
    .min(0)
    .when('erhlSavingsAmount', ([erhlSavingsAmount], schema) => {
      if (!erhlSavingsAmount || erhlSavingsAmount <= 0) return schema.optional();
      return schema.max(erhlSavingsAmount, 'Gifted funds must be less than additional contributions amount').required();
    })
    .label('Gifted amount'),
});
